/**
 * Décimal exact pour les montants (ADR-056 §1-§2) : un entier mis à l'échelle, jamais un `number`.
 * Une quantité stockée en `number` y entre par sa représentation décimale la plus courte.
 */
const DECIMAL_TEXT = /^(-?)(\d+)(?:\.(\d+))?$/;
export function decimal(value) {
    const text = typeof value === 'number' ? numberText(value) : value;
    const match = DECIMAL_TEXT.exec(text);
    if (match === null)
        throw new Error(`« ${text} » n'est pas un nombre décimal.`);
    const fraction = match[3] ?? '';
    const units = BigInt(`${match[2]}${fraction}`) * (match[1] === '-' ? -1n : 1n);
    return { units, scale: fraction.length };
}
/** Représentation décimale la plus courte d'un nombre fini, sans notation exponentielle. */
function numberText(value) {
    if (!Number.isFinite(value))
        throw new Error('Un montant ne se calcule pas sur une valeur infinie.');
    const text = String(value);
    if (!/e/i.test(text))
        return text;
    const [mantissa, exponentText] = text.toLowerCase().split('e');
    const exponent = Number(exponentText);
    const negative = mantissa.startsWith('-');
    const digits = mantissa.replace('-', '').replace('.', '');
    const point = mantissa.replace('-', '').indexOf('.');
    const integerLength = (point < 0 ? mantissa.replace('-', '').length : point) + exponent;
    const body = integerLength <= 0 ? `0.${'0'.repeat(-integerLength)}${digits}` : integerLength >= digits.length ? `${digits}${'0'.repeat(integerLength - digits.length)}` : `${digits.slice(0, integerLength)}.${digits.slice(integerLength)}`;
    return `${negative ? '-' : ''}${body}`;
}
function aligned(a, b) {
    const scale = Math.max(a.scale, b.scale);
    return [a.units * 10n ** BigInt(scale - a.scale), b.units * 10n ** BigInt(scale - b.scale), scale];
}
export function add(a, b) {
    const [x, y, scale] = aligned(a, b);
    return { units: x + y, scale };
}
export function multiply(a, b) {
    return { units: a.units * b.units, scale: a.scale + b.scale };
}
/**
 * Quotient arrondi à `scale` décimales (demi-unité s'éloignant de zéro, ADR-056 §2) ; `null` si le
 * diviseur est nul. Pour un KPI monétaire (LO4) : une moyenne en euros reste exacte au centime.
 */
export function divide(a, b, scale) {
    if (b.units === 0n)
        return null;
    // a / b = (a.units × 10^b.scale) / (b.units × 10^a.scale) ; mis à l'échelle `scale`.
    const numerator = a.units * 10n ** BigInt(b.scale + scale);
    const denominator = b.units * 10n ** BigInt(a.scale);
    const negative = numerator < 0n !== denominator < 0n;
    const n = numerator < 0n ? -numerator : numerator;
    const d = denominator < 0n ? -denominator : denominator;
    let quotient = n / d;
    if ((n % d) * 2n >= d)
        quotient += 1n;
    return { units: negative ? -quotient : quotient, scale };
}
export function negate(a) {
    return { units: -a.units, scale: a.scale };
}
export function isZero(a) {
    return a.units === 0n;
}
/** Arrondi à `scale` décimales, une demi-unité s'éloignant de zéro (arrondi commercial, ADR-056 §2). */
export function round(a, scale) {
    if (a.scale <= scale)
        return { units: a.units * 10n ** BigInt(scale - a.scale), scale };
    const divisor = 10n ** BigInt(a.scale - scale);
    const negative = a.units < 0n;
    const magnitude = negative ? -a.units : a.units;
    let quotient = magnitude / divisor;
    if ((magnitude % divisor) * 2n >= divisor)
        quotient += 1n;
    return { units: negative ? -quotient : quotient, scale };
}
/** Texte décimal à l'échelle de la valeur (`274.89`, `-12.00`). */
export function toText(a) {
    const negative = a.units < 0n;
    const digits = (negative ? -a.units : a.units).toString().padStart(a.scale + 1, '0');
    const body = a.scale === 0 ? digits : `${digits.slice(0, -a.scale)}.${digits.slice(-a.scale)}`;
    return `${negative ? '-' : ''}${body}`;
}
export function compare(a, b) {
    const [x, y] = aligned(a, b);
    return x === y ? 0 : x < y ? -1 : 1;
}
/** Unités mineures d'une devise ISO 4217 ; 2 par défaut. */
const MINOR_UNITS = { JPY: 0, KRW: 0, CLP: 0, ISK: 0, XOF: 0, XAF: 0, XPF: 0, BHD: 3, KWD: 3, OMR: 3, JOD: 3, TND: 3 };
export function minorUnits(currency) {
    return MINOR_UNITS[currency] ?? 2;
}
/**
 * Nombre approché d'un décimal exact, pour un affichage ou un ratio de KPI seulement (ADR-095) :
 * la valeur est d'abord calculée exactement (entier mis à l'échelle), puis convertie **une seule
 * fois** ici. Le résultat ne revient jamais dans un calcul de montant : la valeur exacte voyage à
 * côté (`exact_value`). Seul point de conversion admis par R2.
 */
export function toApproximateNumber(a) {
    return Number(a.units) / 10 ** a.scale;
}
//# sourceMappingURL=decimal.js.map